import React, { useState, useCallback } from 'react';
import propTypes from 'prop-types';
import posed, { PoseGroup } from 'react-pose';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { NavLink } from 'react-router-dom';
import classNames from 'classnames';
import { UserCard } from './UserCard';

const SWITCH_ACCOUNT_ROUTE = {
  icon: <FontAwesomeIcon icon={['fal', 'users-cog']} size="lg" />,
  label: 'Switch Account',
  to: '/account-selection',
};

const PROFILE_ROUTE = {
  icon: <FontAwesomeIcon icon={['fal', 'user']} size="lg" />,
  label: 'My Account',
  to: '/my-account',
};

const SCHEDULED_TASKS_ROUTE = {
  icon: <FontAwesomeIcon icon={['fal', 'sliders-h']} size="lg" />,
  label: 'Scheduled Tasks',
  to: '/scheduled-tasks',
};

// const CONTACT_INFO_ROUTE = {
//   icon: <FontAwesomeIcon icon={['fal', 'address-book']} size="lg" />,
//   label: 'FI Settings',
//   to: '/fi-settings',
// };

const SETTINGS_ROUTE = {
  icon: <FontAwesomeIcon icon={['fal', 'cog']} size="lg" />,
  label: 'Settings',
  to: '/settings',
};

const LOGIN_HISTORY_ROUTE = {
  icon: <FontAwesomeIcon icon={['fal', 'history']} size="lg" />,
  label: 'Login History',
  to: '/login-history',
};

const BANK_ADMIN_ROUTES = [
  SCHEDULED_TASKS_ROUTE,
  PROFILE_ROUTE,
  LOGIN_HISTORY_ROUTE,
  // SETTINGS_ROUTE,
];

const PRIMARY_BANK_ADMIN_ROUTES = [
  PROFILE_ROUTE,
  LOGIN_HISTORY_ROUTE,
  SETTINGS_ROUTE,
  SCHEDULED_TASKS_ROUTE,

  {
    icon: <FontAwesomeIcon icon={['fal', 'user-friends']} size="lg" />,
    label: 'Users',
    to: '/users',
  },
];

const COMMON_ROUTES = [PROFILE_ROUTE, LOGIN_HISTORY_ROUTE];

const AccountMenuWrapper = posed.div({
  enter: {
    opacity: 1,
  },
  exit: {
    opacity: 0,
    applyAtStart: {
      position: 'fixed',
    },
  },
});

const AccountMenuList = posed.ul({
  enter: {
    staggerChildren: 50,
    staggerDirection: -1,
    opacity: 1,
    delay: 200,
    delayChildren: 200,
  },
  exit: {
    opacity: 0,
  },
});

const AccountMenuItem = posed.li({
  enter: {
    x: 0,
  },
  exit: {
    x: '-100%',
  },
});

export function AccountMenu({ user, onSignOut, isCondensed }) {
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const onToggleMenu = useCallback(() => {
    setAccountMenuOpen(!accountMenuOpen);
  }, [accountMenuOpen]);
  const wrapperClassNames = classNames('account-menu', {
    'is-condensed': isCondensed,
  });

  let routes = [...COMMON_ROUTES];
  if (user.isBankAdmin) {
    routes = [...BANK_ADMIN_ROUTES];

    if (user.isPrimaryBankAdmin) {
      routes = [...PRIMARY_BANK_ADMIN_ROUTES];
    }
  }
  if (user.accounts.length > 1 && !user.isBankAdmin && !user.isPrimaryAdmin) {
    routes.unshift(SWITCH_ACCOUNT_ROUTE);
  }

  /* eslint-disable jsx-a11y/anchor-is-valid, jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */
  return (
    <>
      <UserCard user={user} isCondensed={isCondensed} onClick={onToggleMenu} />
      <PoseGroup>
        {accountMenuOpen && (
          <AccountMenuWrapper className={wrapperClassNames} key="menu">
            <button
              type="button"
              className="icon is-large close"
              onClick={onToggleMenu}
            >
              <FontAwesomeIcon icon={['fal', 'times-circle']} size="2x" />
            </button>
            <AccountMenuList className="menu-list">
              {routes.map(route => (
                <AccountMenuItem key={route.label}>
                  <NavLink className="menu-link" to={route.to}>
                    <span className="icon">{route.icon}</span>
                    <span>{route.label}</span>
                  </NavLink>
                </AccountMenuItem>
              ))}
              <AccountMenuItem>
                <a onClick={onSignOut} className="menu-link logout">
                  <span className="icon">
                    <FontAwesomeIcon icon={['fal', 'door-open']} size="lg" />
                  </span>
                  <span>Logout</span>
                </a>
              </AccountMenuItem>
            </AccountMenuList>
            <UserCard
              user={user}
              isCondensed={isCondensed}
              onClick={onToggleMenu}
            />
          </AccountMenuWrapper>
        )}
      </PoseGroup>
    </>
  );
}

AccountMenu.propTypes = {
  user: propTypes.object.isRequired,
  isCondensed: propTypes.bool.isRequired,
  onSignOut: propTypes.func.isRequired,
};
